/**
 * TemplateSelectorField
 *
 * Schema-driven-form adapter that wraps `TemplateSelector` so it can be used
 * as a `'custom'` field in an `EntityForm` layout. Bridges `controllerField`
 * (react-hook-form) into the standalone selector's `value` / `onChange` API.
 *
 * Optional pre-filter is supplied via `TemplatePrefilterContext` so callers
 * (e.g. checklist creation) can scope the visible templates without having
 * to override the field's component.
 */
import { FieldComponentProps } from '@/components/forms/fields/types';
import { Skeleton } from '@/components/ui/skeleton';
import { createContext, useContext } from 'react';
import { TemplateSelector } from './TemplateSelector';

export const TemplatePrefilterContext = createContext<string[] | undefined>(
  undefined,
);

export function TemplateSelectorField({
  formMode,
  controllerField,
  field,
}: FieldComponentProps) {
  const prefilterTags = useContext(TemplatePrefilterContext);

  if (formMode === 'loading') {
    return <Skeleton className="h-8 w-full" />;
  }

  // Read-only / view mode: show the same selector but disabled, so the
  // current value is still visible.
  const disabled = formMode === 'view';

  return (
    <TemplateSelector
      value={(controllerField.value as string | null) ?? null}
      onChange={controllerField.onChange}
      placeholder={field.meta.placeholder}
      disabled={disabled}
      noneLabel="Blank checklist (no template)"
      prefilterTags={prefilterTags}
    />
  );
}
